import React from "react";
import { createGlobalStyle } from "styled-components";

/**
 * `metadata-import` opt-in theme — the Customer Portal palette for the Metadata Import &
 * Enrichment interface, applied ONLY to `metadata-import/*` routes (wrapped there in
 * `AppRouter.tsx`), exactly mirroring how `SpotterThemeProvider` scopes `--spotter*` and
 * `VaultThemeProvider` scopes `--ab*`.
 *
 * Deliberately a SEPARATE namespace (`--mi*`) from Spotter's `--spotter*`: this is a distinct
 * product/interface. It reuses the SAME shared neutral ramp (`--dark*`/`--gray*`/`--green*`/
 * `--red*` from `@cinesys/ui`'s `ColorProvider`, mounted at `:root`) so it reads as a sibling of
 * Spotter/AB Vault, but takes a CineSys-blue accent instead of Spotter's amber so the two products
 * are visually distinguishable. Only the tones this interface owns exclusively (the blue accent
 * family and a couple of status tones) are hardcoded here.
 *
 * The app runs `color-scheme: dark` globally (see `CustomerTheme.tsx`), so there is only a
 * dark-mode set here — no light variant to support.
 *
 * SPIKE NOTE: this whole `metadata-import/` interface is a frontend-only UI spike over mocked
 * data (no metadata writes, no persistence). It is self-contained and easy to remove.
 */
export const MetadataImportGlobalStyle = createGlobalStyle`
  /* On \`body\` (route-scoped: unmounts when navigating off a metadata-import route) so the
   * background covers the full viewport regardless of content height — same reasoning as
   * SpotterThemeProvider. */
  body {
    background: var(--miPaper);
  }

  :root {
    /* Neutrals aliased from the shared ColorProvider ramp (lossless — same ports Spotter uses). */
    --miPaper: var(--dark060);
    --miSlate: var(--dark090);        /* header bar */
    --miSurface: var(--dark100);      /* cards / panels */
    --miRaised: var(--dark200);       /* raised chips / table header stripe */
    --miInk: var(--gray400);
    --miInkSoft: var(--gray200);
    --miInkFaint: var(--dark700);
    --miLine: var(--dark300);
    --miLine2: var(--dark400);

    /* Product accent — CineSys blue (distinct from Spotter's amber). Soft/line follow the same
     * muted-visibility tuning Spotter applies to its amber accent family. */
    --miAccent: #3e92cc;
    --miAccentBright: #57a8de;
    --miAccentSoft: #16303f;
    --miAccentLine: #2a4d61;
    /* Select-menu row hover/focus fill — a translucent wash of the accent, deliberately DISTINCT
     * from the raised menu background so hovered/keyboard-focused options are visible (mirrors
     * Spotter's --spotterAccentHover). Drives @cinesys/ui's --selectOptionHover. */
    --miAccentHover: rgba(62, 146, 204, 0.28);
    --selectOptionHover: var(--miAccentHover);

    /* Workflow tones — Collection Sync reads on the blue accent; Asset Import gets its own cooler
     * indigo/violet so the two targeting modes are colour-coded consistently everywhere. */
    --miSync: #3e92cc;
    --miSyncSoft: #16303f;
    --miImport: #8f8fe0;
    --miImportSoft: #22223a;
    --miImportLine: #3d3d5e;

    /* Strategy tones — Append = additive/safe (teal-green), Overwrite = replace (amber),
     * Rewrite = destructive (red). Rewrite is shown but not built in this spike. */
    --miAppend: #6fbf95;
    --miAppendSoft: #16281f;
    --miOverwrite: #e0a23a;
    --miOverwriteSoft: #2a2417;
    --miRewrite: #e2604a;
    --miRewriteSoft: #2a1a16;

    /* Status tones. */
    --miOk: #6fbf95;
    --miOkSoft: var(--greenBackground);
    --miOkLine: var(--greenBorder);
    --miWarn: #e0a23a;
    --miWarnSoft: #2a2417;
    --miWarnLine: #4a3d26;
    --miDanger: #e2604a;
    --miDangerSoft: var(--redBackground);
    --miDangerLine: var(--redBorderOverlay);
    --miMuted: #8794a0;
    --miMutedSoft: #22282e;

    --miCodeBg: #20252a;
    --miShadow: 0 1px 2px rgba(0, 0, 0, 0.22), 0 3px 8px rgba(0, 0, 0, 0.22);

    /* Header tones — shared with Spotter/AB via ColorProvider's --headerCrumb* tokens. */
    --miHeaderMuted: var(--headerCrumbMuted);
    --miHeaderStrong: var(--headerCrumbStrong);
    --miHeaderBarTrack: #2c343c;
  }
`;

interface Props {
  children?: React.ReactNode;
}

const MetadataImportThemeProvider = ({ children }: Props) => (
  <>
    <MetadataImportGlobalStyle />
    {children}
  </>
);

export default MetadataImportThemeProvider;
